import type {
  ApiResponse,
  AdminMerchantOrderDefaultsDetail,
  AdminMerchantOrderDefaultsRefreshResult,
  AdminMerchantSnapshotRecomputeResult,
  Merchant,
  MerchantChainAccess,
  MerchantChainCompatibility,
  MerchantChainReviewItem,
  PaginatedResponse,
} from '@/types'
import { adminApiClient } from '@/services/admin/baseService'
import {
  extractDownloadFilename,
  resolveBlobApiErrorMessage,
} from '@/services/admin/baseService'
import { getApiClientHeaderForMode } from '@/utils/apiConfig'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  buildPaginatedResponse,
  extractArrayFromApiPayload,
} from '@/services/admin/paginatedResponseHelpers'

const mapMerchantRecord = (record: any): Merchant => {
  const internalId = record?.merchant_internal_id ?? record?.internalId ?? record?.id_internal ?? record?.internal_id ?? null
  const businessId = record?.merchant_business_id ?? record?.merchantBusinessId ?? record?.merchantId ?? record?.business_id ?? null
  const displayId = internalId ?? record?.id ?? businessId ?? ''
  const normalizeStatus = (status: string | undefined | null): Merchant['status'] => {
    if (!status) return 'mst_pending_config'
    if (status.startsWith('mst_')) return status as Merchant['status']
    switch (status.toLowerCase()) {
      case 'pending_configuration':
      case 'pending_config':
        return 'mst_pending_config'
      case 'partial_active':
        return 'mst_partial_active'
      case 'active':
        return 'mst_active'
      case 'suspended':
        return 'mst_suspended'
      case 'terminated':
        return 'mst_terminated'
      default:
        return 'mst_pending_config'
    }
  }

  return {
    id: displayId,
    merchantId: businessId ?? displayId,
    merchant_id: internalId ?? displayId,
    merchant_internal_id: internalId ?? undefined,
    merchant_business_id: businessId ?? displayId,
    user_id: record?.user_id ?? record?.userId ?? '',
    username: record?.username ?? record?.user_name ?? undefined,
    name: record?.name ?? record?.merchant_name ?? 'Unknown Merchant',
    description: record?.description ?? null,
    status: normalizeStatus(record?.status ?? record?.status_id),
    email: record?.email ?? undefined,
    api_key: record?.api_key ?? undefined,
    api_key_hash: record?.api_key_hash ?? undefined,
    webhook_url: record?.webhook_url ?? undefined,
    webhook_secret: record?.webhook_secret ?? undefined,
    settlement_addresses: record?.settlement_addresses ?? undefined,
    wallet_connection_status: record?.wallet_connection_status ?? undefined,
    last_wallet_update: record?.last_wallet_update ?? undefined,
    created_at: record?.created_at ?? record?.createdAt ?? new Date().toISOString(),
    updated_at: record?.updated_at ?? record?.updatedAt ?? new Date().toISOString(),
  }
}

const normalizePaginatedResponse = <T>(
  payload: ApiResponse<any> | undefined,
  options: { keys?: string[]; defaultPage?: number; defaultLimit?: number } = {}
): PaginatedResponse<T> => {
  const payloadAny = payload as any
  const items = extractArrayFromApiPayload<T>(payloadAny, {
    keys: options.keys,
    candidateKeys: ['items', 'records', 'deployments', 'merchants'],
    includePagination: true,
  })
  return buildPaginatedResponse(payload, items, options)
}

export interface MerchantListParams {
  page?: number
  limit?: number
  pageSize?: number
  search?: string
  status?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface MerchantChainReviewListParams {
  page?: number
  pageSize?: number
  approvalStatus?: string
  chainCompatibility?: MerchantChainCompatibility
  merchantStatus?: string
  keyword?: string
}

type ModeAwareRequestConfig = {
  headers: Record<string, string>
  __preserveAppClientHeader: boolean
}

const buildModeAwareConfig = (
  mode?: NetworkEnvironment
): ModeAwareRequestConfig | undefined => {
  if (!mode) {
    return undefined
  }
  return {
    headers: {
      'X-App-Client': getApiClientHeaderForMode(mode),
    },
    __preserveAppClientHeader: true,
  }
}

const mapMerchantChainAccess = (record: any): MerchantChainAccess => ({
  compatibility: record?.compatibility,
  reviewRevisionId: record?.reviewRevisionId ?? null,
  requestedEnabled: Boolean(record?.requestedEnabled),
  planAllowed: Boolean(record?.planAllowed ?? record?.plan_allowed ?? record?.compatibility === 'evm'),
  ownershipVerified: Boolean(record?.ownershipVerified),
  approvalStatus: record?.approvalStatus ?? 'not_submitted',
  effectiveEnabled: Boolean(record?.effectiveEnabled),
  canDisable: Boolean(record?.canDisable),
  reviewSubmittedAt: record?.reviewSubmittedAt ?? null,
  approvedAt: record?.approvedAt ?? null,
  approvedBy: record?.approvedBy ?? null,
  rejectedAt: record?.rejectedAt ?? null,
  rejectedBy: record?.rejectedBy ?? null,
  rejectionReason: record?.rejectionReason ?? null,
  lastEnabledAt: record?.lastEnabledAt ?? null,
  lastDisabledAt: record?.lastDisabledAt ?? null,
  walletAddress: record?.walletAddress ?? null,
  walletVerifiedAt: record?.walletVerifiedAt ?? null,
})

export const merchantService = {
  async getAllMerchants(
    params?: MerchantListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<Merchant>> {
    const searchParams = new URLSearchParams()

    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) {
      searchParams.append('pageSize', params.limit.toString())
    } else if (params?.pageSize) {
      searchParams.append('pageSize', params.pageSize.toString())
    }
    if (params?.search) searchParams.append('search', params.search)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)

    const query = searchParams.toString()
    const url = query ? `/admin/merchants?${query}` : '/admin/merchants'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    const normalized = normalizePaginatedResponse<any>(response.data, {
      keys: ['merchants'],
      defaultPage: params?.page,
      defaultLimit: params?.limit ?? params?.pageSize,
    })

    return {
      ...normalized,
      data: (normalized.data ?? []).map(record => mapMerchantRecord(record)),
    }
  },

  async exportCsv(
    params?: MerchantListParams,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>('/admin/merchants/export', {
        params,
        responseType: 'blob',
        ...buildModeAwareConfig(mode),
      })

      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `merchants-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export merchants'))
    }
  },

  async getMerchantById(merchantId: string, mode?: NetworkEnvironment): Promise<Merchant> {
    try {
      const response = await adminApiClient.get<ApiResponse<any>>(
        `/admin/merchants/${merchantId}`,
        buildModeAwareConfig(mode)
      )

      const payload = response?.data?.data
      if (payload?.merchant) {
        const mapped = mapMerchantRecord(payload.merchant)
        if (!mapped.merchant_internal_id) {
          mapped.merchant_internal_id = merchantId
        }
        if (!mapped.merchant_id) {
          mapped.merchant_id = merchantId
        }
        if (!mapped.id) {
          mapped.id = merchantId
        }
        return mapped
      }

      if (payload) {
        const mapped = mapMerchantRecord(payload)
        if (!mapped.merchant_internal_id) {
          mapped.merchant_internal_id = merchantId
        }
        if (!mapped.merchant_id) {
          mapped.merchant_id = merchantId
        }
        if (!mapped.id) {
          mapped.id = merchantId
        }
        return mapped
      }

      throw new Error(`Merchant ${merchantId} API returned no data`)
    } catch (error) {
      console.error(`Error fetching merchant ${merchantId}:`, error)
      const apiMessage =
        (error as any)?.response?.data?.error?.message ??
        (error as any)?.response?.data?.message ??
        (error instanceof Error ? error.message : null)
      throw new Error(apiMessage || `Failed to fetch merchant ${merchantId}`)
    }
  },

  async updateMerchantStatus(
    merchantId: string,
    status: string,
    reason?: string,
    mode?: NetworkEnvironment
  ): Promise<void> {
    await adminApiClient.put(
      `/admin/merchants/${merchantId}/status`,
      {
        status,
        reason,
      },
      buildModeAwareConfig(mode)
    )
  },

  async getMerchantChainReviews(
    params?: MerchantChainReviewListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<MerchantChainReviewItem>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString())
    if (params?.approvalStatus) searchParams.append('approvalStatus', params.approvalStatus)
    if (params?.chainCompatibility) {
      searchParams.append('chainCompatibility', params.chainCompatibility)
    }
    if (params?.merchantStatus) searchParams.append('merchantStatus', params.merchantStatus)
    if (params?.keyword) searchParams.append('keyword', params.keyword)

    const query = searchParams.toString()
    const url = query ? `/admin/merchant-chain-reviews?${query}` : '/admin/merchant-chain-reviews'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    const normalized = normalizePaginatedResponse<any>(response.data, {
      keys: ['reviews'],
      defaultPage: params?.page,
      defaultLimit: params?.pageSize,
    })

    return {
      ...normalized,
      data: (normalized.data ?? []).map((record) => ({
        merchant: {
          id: record?.merchant?.id ?? '',
          merchantId: record?.merchant?.merchantId ?? null,
          name: record?.merchant?.name ?? null,
          status: mapMerchantRecord({
            id: record?.merchant?.id ?? '',
            merchantBusinessId: record?.merchant?.merchantId ?? '',
            name: record?.merchant?.name ?? '',
            status: record?.merchant?.status ?? null,
            user_id: '',
          }).status,
        },
        chainAccess: mapMerchantChainAccess(record?.chainAccess ?? {}),
      })),
    }
  },

  async getMerchantChains(
    merchantId: string,
    mode?: NetworkEnvironment
  ): Promise<{ merchant: Merchant; chains: MerchantChainAccess[] }> {
    const response = await adminApiClient.get<ApiResponse<any>>(
      `/admin/merchants/${merchantId}/chains`,
      buildModeAwareConfig(mode)
    )

    const payload = response?.data?.data
    return {
      merchant: mapMerchantRecord(payload?.merchant ?? { id: merchantId, user_id: '' }),
      chains: Array.isArray(payload?.chains)
        ? payload.chains.map((record: any) => mapMerchantChainAccess(record))
        : [],
    }
  },

  async approveMerchantChain(
    merchantId: string,
    compatibility: MerchantChainCompatibility,
    reviewRevisionId: string,
    mode?: NetworkEnvironment
  ): Promise<{ queued: boolean }> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/merchants/${merchantId}/chains/${compatibility}/approve`,
      { reviewRevisionId },
      buildModeAwareConfig(mode)
    )
    const payload = response?.data?.data
    return { queued: payload?.queued === true }
  },

  async rejectMerchantChain(
    merchantId: string,
    compatibility: MerchantChainCompatibility,
    reason: string,
    reviewRevisionId: string,
    mode?: NetworkEnvironment
  ): Promise<{ queued: boolean }> {
    const response = await adminApiClient.post<ApiResponse<any>>(
      `/admin/merchants/${merchantId}/chains/${compatibility}/reject`,
      { reason, reviewRevisionId },
      buildModeAwareConfig(mode)
    )
    const payload = response?.data?.data
    return { queued: payload?.queued === true }
  },

  async getMerchantOrderDefaults(
    merchantId: string,
    mode?: NetworkEnvironment
  ): Promise<AdminMerchantOrderDefaultsDetail> {
    const response = await adminApiClient.get<ApiResponse<AdminMerchantOrderDefaultsDetail>>(
      `/admin/merchants/${merchantId}/order-defaults`,
      buildModeAwareConfig(mode)
    )
    if (!response.data?.data) {
      throw new Error(`Merchant ${merchantId} order defaults API returned no data`)
    }
    return response.data.data
  },

  async refreshMerchantOrderDefaults(
    merchantId: string,
    mode?: NetworkEnvironment
  ): Promise<AdminMerchantOrderDefaultsRefreshResult> {
    const response = await adminApiClient.post<ApiResponse<AdminMerchantOrderDefaultsRefreshResult>>(
      `/admin/merchants/${merchantId}/order-defaults/refresh`,
      {},
      buildModeAwareConfig(mode)
    )
    if (!response.data?.data) {
      throw new Error(`Merchant ${merchantId} refresh API returned no data`)
    }
    return response.data.data
  },

  async recomputeMerchantSnapshot(
    merchantId: string,
    mode?: NetworkEnvironment
  ): Promise<AdminMerchantSnapshotRecomputeResult> {
    const response = await adminApiClient.post<ApiResponse<AdminMerchantSnapshotRecomputeResult>>(
      `/admin/merchants/${merchantId}/snapshot/recompute`,
      {},
      buildModeAwareConfig(mode)
    )
    if (!response.data?.data) {
      throw new Error(`Merchant ${merchantId} snapshot recompute API returned no data`)
    }
    return response.data.data
  },
}
